import { ClientEvent, EventTimeline, MatrixEvent, Room, RoomStateEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { RoleAssignment, WorkspaceRole, WorkspaceRoleV1, WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import normalizeRole from './normalizeRole';

type ReadableRole = WorkspaceRoleV1 | WorkspaceRoleV2;

const isRoleLike = (item: unknown): item is { id: string; name: string } =>
  typeof item === 'object' &&
  item !== null &&
  'id' in item &&
  typeof (item as { id: unknown }).id === 'string' &&
  'name' in item &&
  typeof (item as { name: unknown }).name === 'string';

const hasV1PowerLevel = (item: object): item is { powerLevel: number } =>
  'powerLevel' in item && typeof (item as { powerLevel: unknown }).powerLevel === 'number';

const hasV2PowerLevels = (
  item: object
): item is { powerLevels: { workspace: number; chat: number; meeting: number } } => {
  if (!('powerLevels' in item)) return false;
  const pls = (item as { powerLevels: unknown }).powerLevels;
  return (
    typeof pls === 'object' &&
    pls !== null &&
    typeof (pls as { workspace: unknown }).workspace === 'number' &&
    typeof (pls as { chat: unknown }).chat === 'number' &&
    typeof (pls as { meeting: unknown }).meeting === 'number'
  );
};

export const areValidRoles = (data: unknown): data is ReadableRole[] =>
  Array.isArray(data) && data.every((item) => isRoleLike(item) && (hasV1PowerLevel(item) || hasV2PowerLevels(item)));

const isAssignmentV2Shape = (item: unknown): item is RoleAssignment => {
  if (typeof item !== 'object' || item === null) return false;
  if ('userId' in item) return false;
  const candidate = item as Record<string, unknown>;
  if (!('matrixId' in candidate)) return false;
  if (candidate.matrixId !== null && typeof candidate.matrixId !== 'string') return false;
  return typeof candidate.roleId === 'string' && typeof candidate.assignedBy === 'string';
};

export const areValidAssignments = (data: unknown): data is RoleAssignment[] =>
  Array.isArray(data) && data.every(isAssignmentV2Shape);

const sanitizeInherited = (role: ReadableRole, roleId: string): ReadableRole => {
  if (!('inherited' in role)) return role;
  const inherited = (role as { inherited: unknown }).inherited;
  if (inherited === true || inherited === undefined) return role;

  logger.warn('Workspace role inherited flag invalid; treating as custom', { roleId });
  const { inherited: _stripped, ...rest } = role as ReadableRole & { inherited: unknown };
  return rest as ReadableRole;
};

const useWorkspaceRoles = (workspaceId: string | null) => {
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext({ requireDefinedMatrixClient: false });
  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const latestRoleEventTsRef = useRef(0);

  const clearRoles = useCallback(() => {
    setRoles([]);
    setRoleAssignments([]);
  }, []);

  const refreshRoles = useCallback(() => {
    const workspaceRoom = !!workspaceId && matrixClient?.getRoom(workspaceId);
    if (!matrixClient || !workspaceRoom || !hasReachedSyncReadyState) return clearRoles();

    const state = workspaceRoom.getLiveTimeline().getState(EventTimeline.FORWARDS);
    const roleEvent = state?.getStateEvents(CustomMeedioMatrixEvents.WORKSPACE_ROLES)[0];
    if (!roleEvent) return clearRoles();

    const eventTs = roleEvent.getTs();
    const isMostRecentEvent = eventTs > latestRoleEventTsRef.current;
    if (!isMostRecentEvent) return;

    const content = roleEvent.getContent();

    if (!areValidRoles(content.roles)) {
      logger.warn('Workspace roles event invalid', { content });
      return;
    }
    if (!areValidAssignments(content.assignments)) {
      logger.warn('Workspace roles event invalid', { content });
      return;
    }

    latestRoleEventTsRef.current = eventTs;

    const normalisedRoles = content.roles
      .map((role) => sanitizeInherited(role, role.id))
      .map((role) => normalizeRole(role));

    const sortedRoles = normalisedRoles.sort((a, b) => {
      if (a.defaultRoleType && !b.defaultRoleType) return -1;
      if (!a.defaultRoleType && b.defaultRoleType) return 1;
      return b.powerLevels.workspace - a.powerLevels.workspace;
    });

    setRoles(sortedRoles);
    setRoleAssignments(content.assignments);
  }, [matrixClient, workspaceId, hasReachedSyncReadyState, clearRoles]);

  useEffect(() => {
    latestRoleEventTsRef.current = 0;
    refreshRoles();
  }, [refreshRoles]);

  useEffect(() => {
    if (!matrixClient || !workspaceId || !hasReachedSyncReadyState) return;

    const handleStateEventUpdate = (event: MatrixEvent) => {
      if (event.getType() === CustomMeedioMatrixEvents.WORKSPACE_ROLES && event.getRoomId() === workspaceId) {
        refreshRoles();
      }
    };

    const handleRoomEvent = (room: Room) => {
      if (room.roomId === workspaceId) refreshRoles();
    };

    matrixClient.on(RoomStateEvent.Events, handleStateEventUpdate);
    matrixClient.on(ClientEvent.Room, handleRoomEvent);

    return () => {
      matrixClient.off(RoomStateEvent.Events, handleStateEventUpdate);
      matrixClient.off(ClientEvent.Room, handleRoomEvent);
    };
  }, [matrixClient, workspaceId, hasReachedSyncReadyState, refreshRoles]);

  return { roles, roleAssignments };
};

export default useWorkspaceRoles;
